import { ART_PALETTE } from './art-palette.js';
import { DAY_NIGHT } from './day-night-config.js';

export function cycleDurationMs(config = DAY_NIGHT) {
  return config.dayDurationMs + config.duskDurationMs + config.nightDurationMs + config.dawnDurationMs;
}

export function wrapElapsedMs(elapsedMs, config = DAY_NIGHT) {
  const cycle = cycleDurationMs(config);
  if (!Number.isFinite(elapsedMs) || cycle <= 0) return 0;
  return ((elapsedMs % cycle) + cycle) % cycle;
}

export function defaultElapsedMs(config = DAY_NIGHT) {
  if (Number.isFinite(config.startOffsetMs)) return wrapElapsedMs(config.startOffsetMs, config);
  return wrapElapsedMs(config.dayDurationMs / 2, config);
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

export function phaseAt(elapsedMs, config = DAY_NIGHT) {
  const elapsed = wrapElapsedMs(elapsedMs, config);
  const day = config.dayDurationMs;
  const dusk = config.duskDurationMs;
  const night = config.nightDurationMs;
  const dawn = config.dawnDurationMs;

  if (elapsed < day) {
    return { phase: 'day', nightStrength: 0, elapsedMs: elapsed, progress: elapsed / cycleDurationMs(config) };
  }
  if (elapsed < day + dusk) {
    const into = elapsed - day;
    return { phase: 'dusk', nightStrength: dusk <= 0 ? 1 : into / dusk, elapsedMs: elapsed, progress: elapsed / cycleDurationMs(config) };
  }
  if (elapsed < day + dusk + night) {
    return { phase: 'night', nightStrength: 1, elapsedMs: elapsed, progress: elapsed / cycleDurationMs(config) };
  }
  const into = elapsed - day - dusk - night;
  return {
    phase: 'dawn',
    nightStrength: dawn <= 0 ? 0 : 1 - into / dawn,
    elapsedMs: elapsed,
    progress: elapsed / cycleDurationMs(config),
  };
}

export function rgbToHsl(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h / 6, s, l];
}

function hueToRgb(p, q, t) {
  let h = t;
  if (h < 0) h += 1;
  if (h > 1) h -= 1;
  if (h < 1 / 6) return p + (q - p) * 6 * h;
  if (h < 1 / 2) return q;
  if (h < 2 / 3) return p + (q - p) * (2 / 3 - h) * 6;
  return p;
}

export function hslToRgb(h, s, l) {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hueToRgb(p, q, h + 1 / 3), hueToRgb(p, q, h), hueToRgb(p, q, h - 1 / 3)];
}

export function parseRgba(hex) {
  return [1, 3, 5, 7].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16));
}

/** Accepts #rrggbb or #rrggbbaa; returns [r, g, b] in 0..1. */
export function parseTint(hex) {
  return [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16) / 255);
}

function toHex([r, g, b, a]) {
  const byte = (value) => Math.round(clamp01(value) * 255).toString(16).padStart(2, '0');
  return `#${byte(r)}${byte(g)}${byte(b)}${byte(a)}`;
}

export function applyNightTransformRgb(r, g, b, grade = DAY_NIGHT.night) {
  const contrast = grade.contrast ?? 1;
  const nr = clamp01((r - 0.5) * contrast + 0.5);
  const ng = clamp01((g - 0.5) * contrast + 0.5);
  const nb = clamp01((b - 0.5) * contrast + 0.5);
  let [h, s, l] = rgbToHsl(nr, ng, nb);
  h = (h + (grade.hueShiftDeg ?? 0) / 360) % 1;
  if (h < 0) h += 1;
  s = clamp01(s * (grade.saturation ?? 1));
  l = clamp01(l * (grade.brightness ?? 1));
  let rgb = hslToRgb(h, s, l);
  if (grade.tint && (grade.tintStrength ?? 0) > 0) {
    const tint = parseTint(grade.tint);
    const k = clamp01(grade.tintStrength);
    rgb = rgb.map((value, channel) => clamp01(value + (tint[channel] - value) * k));
  }
  return rgb;
}

export function transformColor(hex, grade = DAY_NIGHT.night) {
  const [r, g, b, a] = parseRgba(hex);
  const [nr, ng, nb] = applyNightTransformRgb(r / 255, g / 255, b / 255, grade);
  return toHex([nr, ng, nb, a / 255]);
}

function gradedPalette(dayPalette, grade, overrides) {
  return dayPalette.map((hex, index) => {
    const override = overrides?.[index];
    if (override) return override;
    if (index === 0 || index === 1) return hex;
    return transformColor(hex, grade);
  });
}

export function nightPalette(dayPalette = ART_PALETTE, config = DAY_NIGHT) {
  return gradedPalette(dayPalette, config.night, config.paletteOverrides);
}

export function duskPalette(dayPalette = ART_PALETTE, config = DAY_NIGHT) {
  return gradedPalette(dayPalette, config.dusk, config.duskPaletteOverrides);
}

export class DayNightClock {
  constructor(config = DAY_NIGHT) {
    this.config = config;
    this.elapsedMs = defaultElapsedMs(config);
    this.paused = false;
  }

  update(dt) {
    if (this.paused) return this.snapshot();
    this.elapsedMs = wrapElapsedMs(this.elapsedMs + dt * 1000, this.config);
    return this.snapshot();
  }

  snapshot() {
    const state = phaseAt(this.elapsedMs, this.config);
    return { ...state, paused: this.paused };
  }

  get nightStrength() {
    return phaseAt(this.elapsedMs, this.config).nightStrength;
  }

  get phase() {
    return phaseAt(this.elapsedMs, this.config).phase;
  }

  get progress() {
    return phaseAt(this.elapsedMs, this.config).progress;
  }

  setProgress(progress) {
    this.elapsedMs = wrapElapsedMs(progress * cycleDurationMs(this.config), this.config);
    return this.snapshot();
  }

  serialize() {
    return { elapsedMs: this.elapsedMs, paused: this.paused };
  }

  restore(data) {
    if (data && Number.isFinite(data.elapsedMs)) {
      this.elapsedMs = wrapElapsedMs(data.elapsedMs, this.config);
      this.paused = Boolean(data.paused);
    } else {
      this.elapsedMs = defaultElapsedMs(this.config);
      this.paused = false;
    }
    return this.snapshot();
  }

  reset() {
    this.elapsedMs = defaultElapsedMs(this.config);
    this.paused = false;
    return this.snapshot();
  }
}
